import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BookOpen, ArrowLeft, CalendarDays, MapPin, Users, Settings, FileText, Check, GraduationCap, PlayCircle } from "lucide-react";
import { paragraphs } from "./libraryUtils";
import { useMemberAuth } from "@/members/MemberAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso + "T00:00:00").toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

// Card de uma edição publicada (percurso com sessões presenciais).
function EditionCard({ e, onChanged, certificate }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const enroll = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/members/itineraries/editions/${e.id}/enroll`, {}, { withCredentials: true });
      toast.success("Inscrição confirmada! Vai receber um email com o calendário.");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na inscrição.");
    } finally { setBusy(false); }
  };

  const unenroll = async () => {
    if (!window.confirm("Cancelar a sua inscrição neste percurso?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/members/itineraries/editions/${e.id}/unenroll`, {}, { withCredentials: true });
      toast.success("Inscrição cancelada.");
      onChanged();
    } catch { toast.error("Erro."); } finally { setBusy(false); }
  };

  const scheduled = (e.sessions || []).filter((s) => s.date);
  const visible = expanded ? e.sessions : (e.sessions || []).slice(0, 3);

  return (
    <div className={`bg-white border p-6 ${e.enrolled ? "border-[hsl(var(--accent))]" : "border-[hsl(var(--border))]"}`} data-testid={`edition-card-${e.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">{e.title}</div>
          <h3 className="display-heading text-2xl mt-1">{e.itinerary_title}</h3>
        </div>
        {e.enrolled && (
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--accent))]" data-testid={`edition-enrolled-${e.id}`}>
            <Check className="h-3.5 w-3.5" /> Inscrito
          </span>
        )}
      </div>
      {e.description && <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">{e.description}</p>}

      <div className="mt-4 flex flex-wrap gap-4 text-xs text-[hsl(var(--muted-foreground))]">
        <span><CalendarDays className="inline h-3.5 w-3.5 mr-1" />{scheduled.length} de {(e.sessions || []).length} sessões agendadas</span>
        <span><Users className="inline h-3.5 w-3.5 mr-1" />{e.participant_count} participante(s){e.vacancies != null ? ` · ${e.vacancies} vaga(s)` : ""}</span>
      </div>

      <ul className="mt-4 space-y-2">
        {visible.map((s) => (
          <li key={s.index} className="text-sm border-l-2 border-[hsl(var(--border))] pl-3">
            <span className="font-medium">Sessão {s.index} — {s.title}</span>
            {s.date && (
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                {fmtDate(s.date)}{s.time ? ` às ${s.time}` : ""}{s.location ? <span> · <MapPin className="inline h-3 w-3" /> {s.location}</span> : ""}
              </div>
            )}
            {s.educator_name && (
              <div className="text-[11px] text-[hsl(var(--accent))] mt-0.5" data-testid={`session-educator-${e.id}-${s.index}`}>
                Sessão acompanhada por <span className="font-medium text-[hsl(var(--foreground))]">{s.educator_name}</span>
              </div>
            )}
            {e.enrolled && s.doc_url && (
              <a href={s.doc_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] mt-0.5 mr-3" data-testid={`session-doc-${e.id}-${s.index}`}>
                <FileText className="h-3 w-3" /> {s.doc_name || "Documento de estudo"}
              </a>
            )}
            {e.enrolled && s.video_url && (
              <a href={s.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] mt-0.5" data-testid={`session-video-${e.id}-${s.index}`}>
                <PlayCircle className="h-3 w-3" /> Vídeo de preparação
              </a>
            )}
            {s.ppt_url && (
              <a href={s.ppt_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))] mt-0.5 ml-3" data-testid={`session-ppt-${e.id}-${s.index}`}>
                <FileText className="h-3 w-3" /> {s.ppt_name || "PowerPoint da sessão"} (educador)
              </a>
            )}
          </li>
        ))}
      </ul>
      {(e.sessions || []).length > 3 && (
        <button onClick={() => setExpanded(!expanded)} className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
          {expanded ? "Mostrar menos" : `Ver as ${e.sessions.length} sessões`}
        </button>
      )}

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        {certificate && (
          <a href={`${API}/members/itineraries/certificates/${certificate.id}/pdf`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--accent))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--accent))] hover:text-white transition-colors"
            data-testid={`certificate-download-${e.id}`}>
            <GraduationCap className="h-3.5 w-3.5" /> O meu certificado
          </a>
        )}
        {e.enrolled ? (
          <button onClick={unenroll} disabled={busy} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-red-700 disabled:opacity-60" data-testid={`edition-unenroll-${e.id}`}>
            Cancelar inscrição
          </button>
        ) : e.vacancies === 0 ? (
          <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Lotado</span>
        ) : (
          <button onClick={enroll} disabled={busy} className="btn-primary disabled:opacity-60" data-testid={`edition-enroll-${e.id}`}>
            {busy ? "A inscrever…" : "Inscrever-me"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MemberItineraries() {
  const { member } = useMemberAuth();
  const isAssessor = member?.ciees_role === "assessor_espiritual";
  const [items, setItems] = useState([]);
  const [editions, setEditions] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const [lib, ed, ct] = await Promise.all([
        axios.get(`${API}/members/library?kind=itinerary`, { withCredentials: true }),
        axios.get(`${API}/members/itineraries/editions`, { withCredentials: true }),
        axios.get(`${API}/members/itineraries/certificates`, { withCredentials: true }),
      ]);
      setItems(lib.data.items || []);
      setEditions(ed.data.items || []);
      setCerts(ct.data.items || []);
    } catch { toast.error("Erro a carregar os percursos."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Reading view for a single itinerary
  if (active) {
    return (
      <section className="container-page py-12 lg:py-16" data-testid="itinerary-reader">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="itinerary-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </button>
        <div className="overline mt-6">Itinerário</div>
        <h1 className="display-heading text-4xl sm:text-5xl mt-3">{active.title}</h1>
        <span className="accent-rule mt-6 block" />
        <article className="mt-10 prose max-w-3xl text-[hsl(var(--foreground))] leading-relaxed">
          {paragraphs(active.body).map((p, i) => (
            <p key={i} className="mb-5 whitespace-pre-line text-[15px]">{p}</p>
          ))}
        </article>
      </section>
    );
  }

  return (
    <section className="container-page py-12 lg:py-16" data-testid="member-itineraries-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Auto-educação</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Percursos.</h1>
          <span className="accent-rule mt-6 block" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
            Percursos pessoais e comunitários de auto-educação à luz da pedagogia kentenichiana.
          </p>
        </div>
        {isAssessor && (
          <Link to="/membros/percursos/gestao" className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors" data-testid="itineraries-manage-link">
            <Settings className="h-3.5 w-3.5" /> Gerir percursos
          </Link>
        )}
      </div>

      {/* Percursos com sessões presenciais */}
      {editions.length > 0 && (
        <div className="mt-12" data-testid="editions-section">
          <div className="overline">Percursos abertos</div>
          <div className="mt-4 grid lg:grid-cols-2 gap-4">
            {editions.map((e) => (
              <EditionCard key={e.id} e={e} onChanged={load}
                certificate={certs.find((c) => c.edition_id === e.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Textos de leitura (biblioteca) */}
      <div className="mt-12">
        {editions.length > 0 && <div className="overline mb-4">Itinerários — os mapas do caminho</div>}
        {loading ? (
          <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 && editions.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] p-12 text-center" data-testid="itineraries-empty">
            <BookOpen className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
              Os primeiros percursos estarão disponíveis após a Apresentação de Setembro.
            </p>
          </div>
        ) : items.length > 0 && (
          <ul className="grid sm:grid-cols-2 gap-4" data-testid="itineraries-list">
            {items.map((it) => (
              <li key={it.id}>
                <button onClick={() => setActive(it)} className="w-full text-left bg-white border border-[hsl(var(--border))] p-6 hover:border-[hsl(var(--accent))] transition-colors" data-testid={`itinerary-${it.id}`}>
                  <BookOpen className="h-5 w-5 text-[hsl(var(--accent))]" />
                  <h3 className="mt-4 display-heading text-xl leading-tight">{it.title}</h3>
                  {it.description && <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{it.description}</p>}
                  <span className="mt-4 inline-block text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--primary))]">Ler →</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
